import { ArrowRight, BookOpen, CheckCircle, Clock, Star } from "lucide-react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { type CourseWithProgress, useCourses } from "@/hooks/use-courses";

function courseUrl(slug: string): string | null {
  if (slug === "sa-ai-foundations") return "/ai-foundations";
  if (slug === "sa-problem-diagnosis") return "/sa-problem-diagnosis";
  if (slug === "delivery-governance") return "/delivery-governance";
  return null;
}

type ProgressStatus = "not-started" | "in-progress" | "completed";

function getProgressStatus(progress: CourseWithProgress["progress"]): ProgressStatus {
  if (!progress) return "not-started";
  if (progress.completedAt) return "completed";
  return "in-progress";
}

function formatDate(date: Date): string {
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function StatusPill({ status }: { status: ProgressStatus }) {
  if (status === "completed") {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          fontSize: 11,
          fontWeight: 600,
          color: "#166534",
          background: "#dcfce7",
          border: "1px solid #bbf7d0",
          borderRadius: 50,
          padding: "3px 10px",
        }}
      >
        <CheckCircle size={11} />
        Completed
      </span>
    );
  }
  if (status === "in-progress") {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          fontSize: 10,
          fontWeight: 600,
          color: "#1e40af",
          background: "#dbeafe",
          border: "1px solid #bfdbfe",
          borderRadius: 50,
          padding: "2px 8px",
          whiteSpace: "nowrap",
        }}
      >
        <Clock size={9} />
        In Progress
      </span>
    );
  }
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        fontSize: 10,
        fontWeight: 600,
        color: "#608099",
        background: "#f0f4ff",
        border: "1px solid #e0e0e0",
        borderRadius: 50,
        padding: "2px 8px",
        whiteSpace: "nowrap",
      }}
    >
      Not Started
    </span>
  );
}

function FeaturedCourse({ course }: { course: CourseWithProgress }) {
  const status = getProgressStatus(course.progress);
  const url = courseUrl(course.slug);

  const content = (
    <div
      style={{
        background: "linear-gradient(135deg, #0f1944 0%, #1e2d6b 100%)",
        border: "1px solid #2e3f8a",
        borderRadius: 16,
        padding: "28px 32px",
        display: "flex",
        flexDirection: "column",
        gap: 16,
        position: "relative",
        overflow: "hidden",
        transition: "box-shadow 0.15s",
      }}
      className="featured-course-card"
    >
      <div
        style={{
          position: "absolute",
          top: -40,
          right: -40,
          width: 180,
          height: 180,
          borderRadius: "50%",
          background: "rgba(99,102,241,0.12)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: -60,
          right: 60,
          width: 120,
          height: 120,
          borderRadius: "50%",
          background: "rgba(99,102,241,0.08)",
          pointerEvents: "none",
        }}
      />

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Star size={14} fill="#fbbf24" color="#fbbf24" />
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.08em",
            color: "#fbbf24",
            textTransform: "uppercase",
          }}
        >
          Featured Course
        </span>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <h2
          style={{
            fontFamily: "'Raleway', sans-serif",
            fontSize: 22,
            fontWeight: 800,
            color: "#ffffff",
            margin: 0,
            lineHeight: 1.2,
          }}
        >
          {course.title}
        </h2>
        <StatusPill status={status} />
      </div>

      <p style={{ fontSize: 14, color: "#a8b8cc", lineHeight: 1.65, margin: 0, maxWidth: 640 }}>
        {course.description}
      </p>

      <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
        <div
          style={{ display: "flex", alignItems: "center", gap: 6, color: "#7c8db5", fontSize: 13 }}
        >
          <BookOpen size={14} />
          <span>{course.totalModules} modules</span>
        </div>

        {url && (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 13,
              fontWeight: 600,
              color: "#a78bfa",
            }}
          >
            Start learning
            <ArrowRight size={14} />
          </div>
        )}
      </div>
    </div>
  );

  if (url) {
    return (
      <a href={url} style={{ textDecoration: "none", display: "block" }}>
        {content}
      </a>
    );
  }
  return content;
}

function FeaturedCourseSkeleton() {
  return (
    <div
      style={{
        background: "linear-gradient(135deg, #0f1944 0%, #1e2d6b 100%)",
        border: "1px solid #2e3f8a",
        borderRadius: 16,
        padding: "28px 32px",
        display: "flex",
        flexDirection: "column",
        gap: 16,
      }}
    >
      <Skeleton className="h-3 w-28" style={{ background: "rgba(255,255,255,0.1)" }} />
      <Skeleton className="h-7 w-64" style={{ background: "rgba(255,255,255,0.15)" }} />
      <Skeleton className="h-4 w-full" style={{ background: "rgba(255,255,255,0.08)" }} />
      <Skeleton className="h-4 w-4/5" style={{ background: "rgba(255,255,255,0.08)" }} />
      <Skeleton className="h-4 w-24" style={{ background: "rgba(255,255,255,0.08)" }} />
    </div>
  );
}

function CourseCard({ course }: { course: CourseWithProgress }) {
  const status = getProgressStatus(course.progress);
  const url = courseUrl(course.slug);
  const startedAt = course.progress?.startedAt ? new Date(course.progress.startedAt) : null;
  const completedAt = course.progress?.completedAt ? new Date(course.progress.completedAt) : null;

  const inner = (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #e0e0e0",
        borderRadius: 12,
        padding: "20px 24px",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        height: "100%",
        boxSizing: "border-box",
        transition: "box-shadow 0.15s, border-color 0.15s",
      }}
      className="course-card"
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <h3
          style={{
            fontFamily: "'Raleway', sans-serif",
            fontSize: 17,
            fontWeight: 800,
            color: "#0f1944",
            lineHeight: 1.3,
            margin: 0,
          }}
        >
          {course.title}
        </h3>
        <StatusPill status={status} />
      </div>

      <p style={{ fontSize: 13, color: "#608099", lineHeight: 1.6, margin: 0, flexGrow: 1 }}>
        {course.description}
      </p>

      <div
        style={{ display: "flex", alignItems: "center", gap: 6, color: "#608099", fontSize: 12 }}
      >
        <BookOpen size={13} />
        <span>{course.totalModules} modules</span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12 }}>
        {startedAt && (
          <span style={{ color: "#608099" }}>
            <span style={{ fontWeight: 600, color: "#0f1944" }}>Started:</span>{" "}
            {formatDate(startedAt)}
          </span>
        )}
        {completedAt && (
          <span style={{ color: "#608099" }}>
            <span style={{ fontWeight: 600, color: "#166534" }}>Completed:</span>{" "}
            {formatDate(completedAt)}
          </span>
        )}
      </div>
    </div>
  );

  if (url) {
    return (
      <a href={url} style={{ textDecoration: "none", display: "block", height: "100%" }}>
        {inner}
      </a>
    );
  }

  return (
    <div style={{ opacity: 0.6, cursor: "not-allowed", height: "100%" }}>
      {inner}
      <div style={{ marginTop: 8, fontSize: 12, color: "#608099", textAlign: "center" }}>
        Coming Soon
      </div>
    </div>
  );
}

function CourseCardSkeleton() {
  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #e0e0e0",
        borderRadius: 12,
        padding: "20px 24px",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-5 w-20" />
      </div>
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-3 w-24" />
      <Skeleton className="h-3 w-32" />
    </div>
  );
}

export function CoursesPage() {
  const { data: courses, isLoading, isError, error, refetch } = useCourses();

  const featuredCourse = courses?.find((c) => c.isFeatured) ?? null;

  return (
    <div style={{ padding: "8px 0" }}>
      {/* Featured Course */}
      <div style={{ marginBottom: 32 }}>
        {isLoading && <FeaturedCourseSkeleton />}
        {!isLoading && !isError && featuredCourse && <FeaturedCourse course={featuredCourse} />}
      </div>

      {/* My Courses */}
      <div style={{ marginBottom: 24 }}>
        <h1
          style={{
            fontFamily: "'Raleway', sans-serif",
            fontSize: 24,
            fontWeight: 800,
            color: "#0f1944",
            margin: 0,
          }}
        >
          My Courses
        </h1>
        <p style={{ fontSize: 13, color: "#608099", marginTop: 6, marginBottom: 0 }}>
          Training materials to sharpen your client delivery skills.
        </p>
      </div>

      {isLoading && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 16,
          }}
        >
          <CourseCardSkeleton />
          <CourseCardSkeleton />
        </div>
      )}

      {isError && (
        <ErrorState
          message={error instanceof Error ? error.message : "Failed to load courses"}
          onRetry={() => {
            void refetch();
          }}
        />
      )}

      {!isLoading && !isError && courses && courses.length === 0 && (
        <EmptyState
          icon={BookOpen}
          title="No courses available"
          description="Check back soon — new training content is on the way."
        />
      )}

      {!isLoading && !isError && courses && courses.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 16,
          }}
        >
          {courses.map((course) => (
            <CourseCard key={course.id} course={course} />
          ))}
        </div>
      )}

      <style>{`
        .course-card:hover {
          box-shadow: 0 4px 16px rgba(67, 52, 220, 0.1);
          border-color: #c5bef8 !important;
        }
        a:hover .course-card {
          box-shadow: 0 4px 16px rgba(67, 52, 220, 0.1);
          border-color: #c5bef8 !important;
        }
        .featured-course-card:hover {
          box-shadow: 0 8px 32px rgba(67, 52, 220, 0.3);
        }
        a:hover .featured-course-card {
          box-shadow: 0 8px 32px rgba(67, 52, 220, 0.3);
        }
      `}</style>
    </div>
  );
}
